import { useMemo } from 'react';
import { Sprout, Sun, CloudRain, Snowflake, Leaf } from 'lucide-react';
import { motion } from 'framer-motion';

interface SeasonInfo {
  name: string;
  emoji: string;
  message: string;
  icon: typeof Sprout;
  gradient: string;
  accentColor: string;
}

function getSeason(date: Date): SeasonInfo {
  const month = date.getMonth();

  if (month >= 2 && month <= 4) {
    return {
      name: 'Spring',
      emoji: '🌱',
      message: 'Time to sow seeds and prepare your beds!',
      icon: Sprout,
      gradient: 'from-primary/15 via-garden-leaf/10 to-garden-cream/40',
      accentColor: 'border-primary/20',
    };
  }
  if (month >= 5 && month <= 7) {
    return {
      name: 'Summer',
      emoji: '☀️',
      message: 'Keep watering, feeding, and enjoying the harvest!',
      icon: Sun,
      gradient: 'from-garden-sun/20 via-accent/10 to-garden-cream/30',
      accentColor: 'border-garden-sun/20',
    };
  }
  if (month >= 8 && month <= 10) {
    return {
      name: 'Autumn',
      emoji: '🍂',
      message: 'Harvest time! Start preparing for overwintering.',
      icon: Leaf,
      gradient: 'from-accent/15 via-garden-bloom/10 to-garden-cream/30',
      accentColor: 'border-accent/20',
    };
  }
  return {
    name: 'Winter',
    emoji: '❄️',
    message: 'Plan ahead — order seeds and dream of spring!',
    icon: Snowflake,
    gradient: 'from-garden-sky/15 via-muted/10 to-garden-cream/20',
    accentColor: 'border-garden-sky/20',
  };
}

export function SeasonBanner() {
  const season = useMemo(() => getSeason(new Date()), []);
  const Icon = season.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`rounded-2xl bg-gradient-to-r ${season.gradient} border ${season.accentColor} p-5 flex items-center gap-4 shadow-soft`}
    >
      <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-card/80 shadow-soft border border-border/30 text-2xl shrink-0">
        {season.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">{season.name} Gardening</h3>
          <Icon className="h-4 w-4 text-primary/60" />
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">{season.message}</p>
      </div>
    </motion.div>
  );
}
